import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { getData } from '../../../api/indexedDBHandler'
import SummaryTournamentProtocolPDF from './SummaryTournamentProtocolPDF'
import { getLogoUrl } from '../../../api/Logo'
import { dateNowProtocol } from '../../../api/formatDate'

const calculateCountRankings = (pairs) => {
  // 1. Фильтруем только зачётную рыбу (weight > 0) и недисквалифицированные команды
  const validPairs = pairs
    .map((pair) => ({
      ...pair,
      validFishCount: pair.fishList.filter((fish) => fish.weight > 0).length,
      totalValidWeight: pair.fishList.reduce(
        (sum, fish) => (fish.weight > 0 ? sum + fish.weight : sum),
        0
      ),
    }))
    .filter((pair) => !pair.disqualified)

  // 2. Сортируем по количеству зачётной рыбы (по убыванию), затем по общему весу (по убыванию)
  const sortedPairs = [...validPairs].sort((a, b) => {
    if (b.validFishCount !== a.validFishCount) {
      return b.validFishCount - a.validFishCount
    }
    return b.totalValidWeight - a.totalValidWeight
  })

  // 3. Присваиваем места с учетом ничьих
  let rankedPairs = []
  let currentRank = 1

  while (currentRank <= sortedPairs.length) {
    const currentCount = sortedPairs[currentRank - 1].validFishCount
    const sameRankPairs = sortedPairs.filter(
      (p) => p.validFishCount === currentCount
    )

    if (sameRankPairs.length > 1) {
      // Вычисляем средний ранг для ничьих
      const averageRank = currentRank + (sameRankPairs.length - 1) / 2

      rankedPairs.push(
        ...sameRankPairs.map((pair) => ({
          ...pair,
          overallRank: averageRank,
          isTie: sameRankPairs.length > 1,
        }))
      )
      currentRank += sameRankPairs.length
    } else {
      rankedPairs.push({
        ...sortedPairs[currentRank - 1],
        overallRank: currentRank,
        isTie: false,
      })
      currentRank++
    }
  }

  // 4. Добавляем дисквалифицированные команды в конец
  const disqualifiedPairs = pairs
    .filter((pair) => pair.disqualified)
    .map((pair) => ({
      ...pair,
      overallRank: pairs.length + 3,
      validFishCount: 0,
      totalValidWeight: 0,
      isTie: false,
    }))

  return [...rankedPairs, ...disqualifiedPairs]
}

const calculateWeightRankings = (pairs, minWeight, countFish) => {
  // 1. Фильтруем только зачётную рыбу (weight >= minWeight) и недисквалифицированные команды
  const validPairs = pairs
    .map((pair) => {
      // Фильтруем рыбу по минимальному весу
      const validFishes = pair.fishList
        .filter((fish) => fish.weight >= minWeight)
        .sort((a, b) => b.weight - a.weight) // Сортируем по убыванию веса

      // Берем топ N рыб или все, если меньше N
      const topFishes = validFishes.slice(0, countFish)
      const topWeight = topFishes.reduce((sum, fish) => sum + fish.weight, 0)

      return {
        ...pair,
        validFishCount: validFishes.length,
        topFishCount: topFishes.length,
        topFishWeight: topWeight,
        hasEnoughFish: validFishes.length >= countFish,
        fishesForProtocol: topFishes,
      }
    })
    .filter((pair) => !pair.disqualified)

  // 2. Сортируем сначала по наличию достаточного количества рыб, затем по весу топ N
  const sortedPairs = [...validPairs].sort((a, b) => {
    // Команды с достаточным количеством рыб идут выше
    if (a.hasEnoughFish !== b.hasEnoughFish) {
      return b.hasEnoughFish - a.hasEnoughFish
    }

    // Если у обеих команд достаточно рыб - сортируем по весу топ N
    if (a.hasEnoughFish && b.hasEnoughFish) {
      return b.topFishWeight - a.topFishWeight
    }

    // Если у обеих недостаточно рыб - сортируем по количеству рыб, затем по весу
    if (b.validFishCount !== a.validFishCount) {
      return b.validFishCount - a.validFishCount
    }
    return b.topFishWeight - a.topFishWeight
  })

  // 3. Присваиваем места с учетом ничьих
  let rankedPairs = []
  let currentRank = 1

  while (currentRank <= sortedPairs.length) {
    const currentPair = sortedPairs[currentRank - 1]
    const sameRankPairs = sortedPairs.filter(
      (p) =>
        p.hasEnoughFish === currentPair.hasEnoughFish &&
        p.topFishWeight === currentPair.topFishWeight &&
        (currentPair.hasEnoughFish ||
          p.validFishCount === currentPair.validFishCount)
    )

    if (sameRankPairs.length > 1) {
      // Вычисляем средний ранг для ничьих
      const averageRank = currentRank + (sameRankPairs.length - 1) / 2

      rankedPairs.push(
        ...sameRankPairs.map((pair) => ({
          ...pair,
          overallRank: averageRank,
          isTie: sameRankPairs.length > 1,
        }))
      )
      currentRank += sameRankPairs.length
    } else {
      rankedPairs.push({
        ...currentPair,
        overallRank: currentRank,
        isTie: false,
      })
      currentRank++
    }
  }

  // 4. Добавляем дисквалифицированные команды в конец
  const disqualifiedPairs = pairs
    .filter((pair) => pair.disqualified)
    .map((pair) => ({
      ...pair,
      overallRank: pairs.length + 3,
      validFishCount: 0,
      topFishCount: 0,
      topFishWeight: 0,
      hasEnoughFish: false,
      fishesForProtocol: [],
      isTie: false,
    }))

  return [...rankedPairs, ...disqualifiedPairs]
}

const calculateSummaryRankings = (protocolA, protocolB, protocolC) => {
  // Собираем все команды из всех протоколов
  const allTeams = [
    ...protocolA.data,
    ...protocolB.data,
    ...protocolC.data,
  ].reduce((acc, team) => {
    if (!acc.some((t) => t.id === team.id)) {
      acc.push({
        id: team.id,
        teamName: team.teamName,
        sp: team.sp,
        sector: team.sector,
        disqualified: team.disqualified,
        // Общий вес всех рыб для разрешения ничьих
        totalWeight:
          team.fishList?.reduce((sum, fish) => sum + (fish.weight || 0), 0) ||
          0,
        // Самая крупная рыба для разрешения ничьих
        biggestFish: Math.max(
          ...(team.fishList?.map((f) => f.weight || 0) || [0])
        ),
      }) // Закрывающая скобка для push
    } // Закрывающая скобка для if
    return acc
  }, []) // Закрывающая скобка для reduce

  // Сопоставляем баллы из всех протоколов
  const teamsWithScores = allTeams.map((team) => {
    const teamA = protocolA.data.find((t) => t.id === team.id)
    const teamB = protocolB.data.find((t) => t.id === team.id)
    const teamC = protocolC.data.find((t) => t.id === team.id)

    return {
      ...team,
      scoreA: teamA?.overallRank || allTeams.length + 1,
      scoreB: teamB?.overallRank || allTeams.length + 1,
      scoreC: teamC?.overallRank || allTeams.length + 1,
      totalScore:
        (teamA?.overallRank || allTeams.length + 1) +
        (teamB?.overallRank || allTeams.length + 1) +
        (teamC?.overallRank || allTeams.length + 1),
    }
  })

  // Сортируем по общему баллу, затем по общему весу, затем по самой крупной рыбе
  const sortedTeams = [...teamsWithScores].sort((a, b) => {
    if (a.disqualified && !b.disqualified) return 1
    if (!a.disqualified && b.disqualified) return -1
    if (a.disqualified && b.disqualified) return 0

    if (a.totalScore !== b.totalScore) {
      return a.totalScore - b.totalScore
    }

    if (b.totalWeight !== a.totalWeight) {
      return b.totalWeight - a.totalWeight
    }

    return b.biggestFish - a.biggestFish
  })

  // Присваиваем итоговые места с учетом ничьих
  let rankedTeams = []
  let currentRank = 1

  while (currentRank <= sortedTeams.length) {
    const currentTeam = sortedTeams[currentRank - 1]
    const sameRankTeams = sortedTeams.filter(
      (t) =>
        t.totalScore === currentTeam.totalScore &&
        t.totalWeight === currentTeam.totalWeight &&
        t.biggestFish === currentTeam.biggestFish
    )

    if (sameRankTeams.length > 1) {
      const averageRank = currentRank + (sameRankTeams.length - 1) / 2

      rankedTeams.push(
        ...sameRankTeams.map((team) => ({
          ...team,
          finalRank: averageRank,
          isTie: sameRankTeams.length > 1,
        }))
      )
      currentRank += sameRankTeams.length
    } else {
      rankedTeams.push({
        ...currentTeam,
        finalRank: currentRank,
        isTie: false,
      })
      currentRank++
    }
  }

  return rankedTeams
}

const generateSummaryProtocolData = async (id) => {
  // Получаем данные турнира
  const resultsT = await getData('tournaments', 'id', id)
  if (resultsT.length === 0) throw new Error('No tournament data found')
  const { value: tournamentValue } = resultsT[0]

  // Получаем базовые данные команд и рыбы
  const resultsU = await getData('teams', 'id', id)
  if (resultsU.length === 0) throw new Error('No team data found')
  const { value: teamValue } = resultsU[0]

  const resultsS = await getData('stage', 'id', id)
  if (resultsS.length === 0) throw new Error('No stage data found')
  const { value: stageValue } = resultsS[0]
  // const stageArray = Object.values(stageValue).filter(Boolean)
  const stageArray = Object.values(stageValue).filter(
    (teamEntry) => teamEntry !== null && typeof teamEntry === 'object'
  )

  // Формируем пары (общие данные для всех протоколов)
  const pairs = Object.values(teamValue)
    .filter(
      (teamEntry) =>
        teamEntry?.team?.status === 'Заявка одобрена' &&
        teamEntry?.team?.status_location === 1
    )
    .map((team) => {
      const athletes = team.athlete || []
      const allFishLists = stageArray[0]
        .filter((e) => e.teamId === team.team.id)
        .flatMap((e) => e.fishList || [])

      return {
        id: team.team.id,
        teamName: team.team.team_name_ru,
        sp: athletes
          .map(
            (a) =>
              `${a.fio || ''}${a.sports_category ? ` (${a.sports_category})` : ''}`
          )
          .join(', '),
        sector: team.team.sector,
        fishList: allFishLists,
        disqualified: team.team.status === 'Снята',
      }
    })

  // Рассчитываем данные для каждого протокола
  const protocolAData = calculateCountRankings(pairs) // Протокол по количеству
  const protocolBData = calculateWeightRankings(pairs, 1500, 5) // Топ 5 рыб
  const protocolCData = calculateWeightRankings(pairs, 1500, 1) // Биг Фиш

  // Рассчитываем сводные результаты
  const summaryData = calculateSummaryRankings(
    { data: protocolAData },
    { data: protocolBData },
    { data: protocolCData }
  )

  return {
    allLogo: Object.values(tournamentValue.sponsor || {}),
    mainLogo: getLogoUrl(tournamentValue?.logocup),
    data: summaryData,
    administrator: Object.values(tournamentValue.administrator || []),
    tournament: tournamentValue,
    date: dateNowProtocol(new Date()),
    isFinish: tournamentValue.status_cup === 'Турнир завершен',
    protocolAData: { data: protocolAData },
    protocolBData: { data: protocolBData },
    protocolCData: { data: protocolCData },
  }
}
// групповой_протокол_
const uploadPdfToServer = async (blob, id) => {
  const formData = new FormData()
  formData.append(
    'pdf',
    blob,
    `сводный_протокол_${dateNowProtocol(new Date())}.pdf`
  )
  formData.append('id', id)
  formData.append('isPost', 3)
  formData.append('type', 'SummaryProtocol')

  try {
    const response = await fetch(
      'https://www.admin-protocarp.ru/php/uploads/upload.php',
      {
        method: 'POST',
        body: formData,
      }
    )
    return response.ok ? await response.json() : null
  } catch (error) {
    console.error('Upload error:', error)
    return null
  }
}

export default {
  generate: async function (id) {
    try {
      const protocolData = await generateSummaryProtocolData(id)
      const pdfDoc = <SummaryTournamentProtocolPDF {...protocolData} />

      const blob = await pdf(pdfDoc).toBlob()
      const uploadResult = await uploadPdfToServer(blob, id)

      return {
        blob,
        previewUrl: uploadResult || URL.createObjectURL(blob),
        data: protocolData,
      }
    } catch (error) {
      console.error('Generation error:', error)
      throw error
    }
  },
}
